import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/base/JsonLd";
import { mainSiteUrl } from "@/mocks/categories";
import { getArticleBySlug } from "@/mocks/articles";

const siteUrl = import.meta.env.VITE_SITE_URL;

export default function BlogPage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 pb-20 px-4 md:px-6 text-center">
          <h1 className="font-heading text-3xl text-foreground-950 mb-4">Article Not Found</h1>
          <p className="text-foreground-500 mb-6">The article you are looking for does not exist.</p>
          <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-arrow-left-line"></i>
            Back to Homepage
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    author: {
      "@type": "Organization",
      name: article.author,
    },
    image: article.heroImage,
    url: `${siteUrl}/blog/${article.slug}`,
    publisher: {
      "@type": "Organization",
      name: "pandabuy.help",
      url: siteUrl,
    },
    datePublished: "2026-06-29",
    dateModified: "2026-06-29",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${siteUrl}/blog/${article.slug}`,
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={articleSchema} />
      <Navbar />

      <main>
        <section className="relative w-full h-[360px] md:h-[440px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={article.heroImage}
              alt={article.heroImageAlt}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/50"></div>

          <div className="relative z-10 h-full flex items-center justify-center text-center px-4 md:px-6">
            <div className="max-w-3xl">
              <p className="text-white/70 text-xs md:text-sm tracking-[0.2em] uppercase mb-4 font-label">
                In-Depth Guide
              </p>
              <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl text-white leading-tight">
                {article.title}
              </h1>
            </div>
          </div>
        </section>

        <section className="py-4 px-4 md:px-6 bg-background-50 border-b border-background-200/70">
          <div className="max-w-4xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-400 flex-wrap">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                Home
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-600 whitespace-nowrap">
                Blog
              </span>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-600 whitespace-nowrap truncate max-w-[200px]">
                {article.title.length > 40 ? article.title.slice(0, 40) + "..." : article.title}
              </span>
            </nav>
          </div>
        </section>

        <article className="py-12 md:py-20 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
              <div className="lg:w-[65%]">
                {article.sections.map((section, idx) => (
                  <section key={idx} className="mb-14">
                    <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-5">
                      {section.heading}
                    </h2>
                    <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                      {section.content}
                    </p>
                  </section>
                ))}

                <div className="border-t border-background-200/70 pt-8 mt-8">
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line"></i>
                    Back to pandabuy.help Homepage
                  </Link>
                </div>
              </div>

              <aside className="lg:w-[35%]">
                <div className="sticky top-24 space-y-6">
                  <div className="bg-background-100 rounded-xl p-6">
                    <h4 className="font-semibold text-foreground-900 text-sm mb-3">
                      Quick Links
                    </h4>
                    <div className="flex flex-col gap-2.5">
                      <a
                        href={mainSiteUrl}
                        target="_blank"
                        rel="noopener nofollow"
                        className="text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-external-link-line mr-2"></i>
                        Pandabuy Sheet — Main Site
                      </a>
                      <Link
                        to="/"
                        className="text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-home-line mr-2"></i>
                        pandabuy.help Homepage
                      </Link>
                    </div>
                  </div>

                  <div className="bg-primary-100/50 rounded-xl p-6">
                    <h4 className="font-semibold text-foreground-900 text-sm mb-2">
                      Ready to shop?
                    </h4>
                    <p className="text-foreground-500 text-xs mb-4">
                      Browse the complete Pandabuy spreadsheet collection on kaspreadsheet.com
                    </p>
                    <a
                      href={mainSiteUrl}
                      target="_blank"
                      rel="noopener nofollow"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Buy It Now
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}